// src/components/portal/PortalProperty.js

import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getElementClassificationByPropertyUUID,
  updateElementClassification,
} from './../../../helpers/supabaseHelper';
import RoomCard from './RoomCard';
import LoadingSpinner from './LoadingSpinner';
import { validate as uuidValidate } from 'uuid';
import { OptionsContext } from '../../../contexts/OptionsContext'; // Import the context

const PortalProperty = () => {
  const [propertyUUID, setPropertyUUID] = useState(null);
  const [elementClassification, setElementClassification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Consume OptionsContext
  const { options, loading: optionsLoading, error: optionsError } = useContext(OptionsContext);

  const location = useLocation();

  // Extract property_uuid from the URL with validation
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const uuid = queryParams.get('property_uuid');

    if (uuid && uuidValidate(uuid)) {
      setPropertyUUID(uuid);
      console.log('Property UUID:', uuid);
    } else {
      console.warn('Invalid or missing property_uuid in the URL');
      setError('Invalid or missing Property UUID in the URL.');
    }
  }, [location.search]);

  // Fetch the elementClassification data when propertyUUID is available
  useEffect(() => {
    const fetchElementClassificationData = async () => {
      if (!propertyUUID) return;

      setLoading(true);
      setError(null);

      try {
        const elementClassificationData = await getElementClassificationByPropertyUUID(propertyUUID);
        console.log(elementClassificationData);

        if (elementClassificationData) {
          setElementClassification(elementClassificationData);
          console.log('Fetched Element Classification JSON:', elementClassificationData);
        } else {
          throw new Error('elementClassification data is missing.');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchElementClassificationData();
  }, [propertyUUID]);

  // Handler to update any part of a room
  const handleRoomUpdate = async (updatedRoom) => {
    if (!elementClassification) {
      console.error('Element Classification JSON is not loaded yet.');
      return;
    }

    // Create a deep copy to avoid mutating state directly
    const updatedElementClassification = JSON.parse(JSON.stringify(elementClassification));

    // Find the index of the room to update
    const roomIndex = updatedElementClassification.roomClassifications.findIndex(
      (room) => room.roomID === updatedRoom.roomID
    );

    if (roomIndex !== -1) {
      // Update the room
      updatedElementClassification.roomClassifications[roomIndex] = updatedRoom;

      // Update state optimistically
      setElementClassification(updatedElementClassification);

      try {
        // Update the backend
        const updatedProperty = await updateElementClassification(propertyUUID, updatedElementClassification);
        console.log('Room updated successfully:', updatedProperty);
        // Optionally, provide user feedback here (e.g., toast notification)
      } catch (error) {
        console.error('Error updating room:', error);
        alert(`Failed to update room ${updatedRoom.roomID}: ${error.message}`);
        // Revert state in case of an error
        setElementClassification(elementClassification);
      }
    } else {
      console.error('Room not found:', updatedRoom.roomID);
    }
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">


      {/* Display Loading State */}
      {loading && <LoadingSpinner />}

      {/* Display Error State */}
      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 max-w-2xl mx-auto"
          role="alert"
        >
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* Display Options Loading/Error */}
      {optionsLoading && <LoadingSpinner />}
      {optionsError && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 max-w-2xl mx-auto"
          role="alert"
        >
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{optionsError}</span>
        </div>
      )}

      {/* Display Fetched JSON Data */}
      {elementClassification && !loading && !error && !optionsLoading && !optionsError && (
        <div className="max-w-6xl mx-auto">
			<h3 className="text-2xl font-semibold mb-4">Property classification</h3>
			{/* Display Property UUID */}
			{propertyUUID ? (
				<p className="text-left text-md text-gray-700 font-semibold">
				Property UUID: <span className="">{propertyUUID}</span>
				</p>
			) : (
				<p className="text-center text-md text-red-500 mb-4">Property UUID not found.</p>
			)}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div>
				<p>
					<span className="font-semibold">External Wall:</span> {elementClassification.propertyAttributes.propertyDetails.externalWall}
				</p>
				<p>
					<span className="font-semibold">Roof Frame:</span> {elementClassification.propertyAttributes.propertyDetails.roofFrame}
				</p>
				<p>
					<span className="font-semibold">Roof Cover:</span> {elementClassification.propertyAttributes.propertyDetails.roofCover}
				</p>
				</div>
				<div>
				<p>
					<span className="font-semibold">Property Floor Area:</span> {elementClassification.propertyAttributes.totalFloorArea} sqm
				</p>
				<p>
					<span className="font-semibold">Average Ceiling Height:</span> {elementClassification.propertyAttributes.averageCeilingHeight} m
				</p>
				<p>
					<span className="font-semibold">Total Window Surface Area:</span> {elementClassification.propertyAttributes.totalWindowSurfaceArea} sqm
				</p>
				<p>
					<span className="font-semibold">Total Large Doors:</span> {elementClassification.propertyAttributes.totalLargeDoors}
				</p>
				</div>
			</div>

          {/* Rooms Section */}
          <div>
		  	<h3 className="text-2xl font-semibold mb-4">Property rooms</h3>
            {elementClassification.roomClassifications.map((room, index) => (
              <RoomCard
                key={room.roomID} // Use roomID as key for better performance
                room={room}
                onRoomUpdate={handleRoomUpdate} // Pass the unified handler
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalProperty;
